/* eslint-disable max-len */
import React, { useContext } from 'react';
import { Redirect, Route, Switch, useLocation, useParams, useRouteMatch } from 'react-router';

import PageLoader from '../../../../components/transitions/PageLoader/PageLoader';
import {
  DOCUMENT_CAPTURE_ROUTE_NAMES,
  DOCUMENT_KINDS,
  getKindFromUrl,
} from '../DocumentCapture.helpers';
import type { DocumentKindsValue } from '../DocumentCapture.types';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext, FinanceSettlementProviderContext } from '../DocumentCaptureContexts';
import { DocumentCaptureViewContainer } from '../shared/DocumentCaptureViewContainer/DocumentCaptureViewContainer';
import { useDocumentCaptureExperimentDecisions } from '../useDocumentCaptureExperimentDecisions';

import { useGetFinanceData } from './FinanceProviderSection/useGetFinanceData';
import UploadDocumentsSummary from './pages/UploadDocumentsSummary/UploadDocumentsSummary';
import { getDocumentUploadPath, getDocumentUploadRoutes, resolveDocumentTileReactivation } from './UploadDocuments.helpers';

import styles from './UploadDocumentsPage.scss';

const UploadDocumentsBase = () => {
  const { progress: { documents } } = useContext(DocumentCaptureConfirmationProgressContext);

  const { path } = useRouteMatch();

  const SUMMARY_PATH = getDocumentUploadPath({ path });

  const SUMMARY_ROUTE = (
    <Route
      key="summary"
      exact
      component={UploadDocumentsSummary}
      path={SUMMARY_PATH}
    />
  );

  // Document Page Routes
  const {
    ADDITIONAL_DOCUMENTS_ROUTE,
    BANK_STATEMENT_ROUTE,
    DEATH_CERTIFICATE_ROUTE,
    DRIVING_LICENCE_ROUTE,
    FINANCE_SETTLEMENT_LETTER_ROUTE,
    LETTER_OF_CONSENT_ROUTE,
    PASSPORT_ROUTE,
    PROOF_OF_ADDRESS_ROUTE,
    PROOF_OF_PURCHASE_ROUTE,
    SOLICITORS_LETTER_ROUTE,
    V5_ROUTE,
    WILL_ROUTE,
  } = getDocumentUploadRoutes(path, documents);

  const resolveDocumentRoutes = () => {
    const getDocumentProgress = (kind: DocumentKindsValue) => documents?.find((doc) => doc.kind === kind);

    const getDocumentRouteAllowance = (kind: DocumentKindsValue) => {
      const progress = getDocumentProgress(kind);

      if (!progress) {
        return false;
      }

      const hasActionRequired = resolveDocumentTileReactivation(progress);
      const routeFinished = !hasActionRequired && progress.submitted;

      return [
        progress.required,
        !routeFinished,
      ].every(Boolean);
    };

    const allowV5ProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.V5);
    const allowDrivingLicenceRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.DRIVING_LICENCE);
    const allowProofOfPurchaseProgressProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.PROOF_OF_PURCHASE);
    const allowFinanceSettlementLetterProgressProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER);
    const allowBankStatementProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.BANK_STATEMENT);
    const allowDeathCertificateProgressProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.DEATH_CERTIFICATE);
    const allowUtilityBillProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.UTILITY_BILL);
    const allowLetterOfConsentRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.LETTER_OF_CONSENT);
    const allowWillProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.WILL);
    const allowPassportProgressRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.PASSPORT);
    const allowSolicitorsLetterRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.SOLICITORS_LETTER);
    const allowAdditionalDocumentsRoute = getDocumentRouteAllowance(DOCUMENT_KINDS.ADDITIONAL_DOCUMENTS);

    const result: React.ReactElement | React.ReactElement[] = [];
    return result.concat(
      SUMMARY_ROUTE,
      allowV5ProgressRoute && V5_ROUTE ? V5_ROUTE : [],
      allowDrivingLicenceRoute && DRIVING_LICENCE_ROUTE ? DRIVING_LICENCE_ROUTE : [],
      allowProofOfPurchaseProgressProgressRoute && PROOF_OF_PURCHASE_ROUTE ? PROOF_OF_PURCHASE_ROUTE : [],
      allowFinanceSettlementLetterProgressProgressRoute && FINANCE_SETTLEMENT_LETTER_ROUTE ? FINANCE_SETTLEMENT_LETTER_ROUTE : [],
      allowDeathCertificateProgressProgressRoute && DEATH_CERTIFICATE_ROUTE ? DEATH_CERTIFICATE_ROUTE : [],
      allowBankStatementProgressRoute && BANK_STATEMENT_ROUTE ? BANK_STATEMENT_ROUTE : [],
      allowUtilityBillProgressRoute && PROOF_OF_ADDRESS_ROUTE ? PROOF_OF_ADDRESS_ROUTE : [],
      allowLetterOfConsentRoute && LETTER_OF_CONSENT_ROUTE ? LETTER_OF_CONSENT_ROUTE : [],
      allowWillProgressRoute && WILL_ROUTE ? WILL_ROUTE : [],
      allowPassportProgressRoute && PASSPORT_ROUTE ? PASSPORT_ROUTE : [],
      allowSolicitorsLetterRoute && SOLICITORS_LETTER_ROUTE ? SOLICITORS_LETTER_ROUTE : [],
      allowAdditionalDocumentsRoute && ADDITIONAL_DOCUMENTS_ROUTE ? ADDITIONAL_DOCUMENTS_ROUTE : [],
      // Handle any other and redirect to summary, this will also handle the case where user finished section
      // moved to the next page and then clicked back button
      <Redirect key="redirect" to={SUMMARY_PATH} />,
    );
  };

  return (
    <>
      <Switch>
        {resolveDocumentRoutes()}
      </Switch>
    </>
  );
};

const UploadDocuments = () => {
  const { pathname } = useLocation();
  const { isIncodeEligible } = useDocumentCaptureExperimentDecisions();
  const { id: dpEnquiryId } = useContext(DocumentCaptureOfferContext) ?? {};

  const documentRoute = Object.values(DOCUMENT_CAPTURE_ROUTE_NAMES);

  const showDocumentInformation = documentRoute.some((kind) => pathname.includes(`/upload-documents/${kind}`));

  // @ts-ignore
  const { kind } = useParams();

  const docKind = getKindFromUrl(kind);

  const {
    financeAgreement,
    loading: financeLoading,
    setFinanceAgreement,
  } = useGetFinanceData({ dpEnquiryId });

  const isSummaryPage = pathname.includes('/upload-documents') && !kind;

  if (financeLoading) {
    return <div className={styles.pageLoader}><PageLoader /></div>;
  }

  return (
    <FinanceSettlementProviderContext.Provider value={{ financeAgreement, isAutomatedDocumentUpload: isIncodeEligible, setFinanceAgreement }}>
      <DocumentCaptureViewContainer stepper index={2} isSummaryPage={isSummaryPage} kind={docKind} showDocumentInformation={showDocumentInformation}>
        <UploadDocumentsBase />
      </DocumentCaptureViewContainer>
    </FinanceSettlementProviderContext.Provider>
  );
};

export default UploadDocuments;
